//
/* GS://PROTOCOLO
 * Arquivo: src/services/firestoreService.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 2.6.0 – CORE: Sincronia Financeira Ledger 2.0
 * Impacto da Versão: [src/services/firestoreService.js]
 * Data: 25/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [MODIFICADO]: Função `payAccount` refatorada para escrita tripla atômica (Batch Write).
 * - [NOVO]: Gravação simultânea na coleção `financial_ledger` (Extrato 2.0) com status 'paid'.
 * - [INTEGRIDADE]: Garante que o pagamento de uma conta reflita imediatamente como 'paid' no Extrato 2.0.
 */
//

import { db } from '../firebase/config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  limit,
  serverTimestamp,
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';

const functions = getFunctions(undefined, 'southamerica-east1');

/* ========================= COLEÇÕES ======================== */
const categoriesCollectionRef             = collection(db, 'categories');
const suppliersCollectionRef              = collection(db, 'suppliers');
const clientsCollectionRef                = collection(db, 'users');
const usersCollectionRef                  = collection(db, 'users');
const rentalsCollectionRef                = collection(db, 'rentals');
const purchasesCollectionRef              = collection(db, 'purchases');
const financialCategoriesCollectionRef    = collection(db, 'financial_categories');
const financialTransactionsCollectionRef  = collection(db, 'financial_transactions');
const accountsPayableCollectionRef        = collection(db, 'accounts_payable');
const accountsReceivableCollectionRef     = collection(db, 'accounts_receivable');
const financialSummaryCollectionRef       = collection(db, 'financial_summary');
const staffCollectionRef                  = collection(db, 'staff');

/* =========== FUNÇÃO GENÉRICA DE SANEAMENTO ============== */
const sanitizeFirestoreData = (value) => {
  if (Array.isArray(value)) {
    const sanitizedArray = value
      .map((item) => sanitizeFirestoreData(item))
      .filter((item) => item !== undefined);
    return sanitizedArray;
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const result = {};
    Object.entries(value).forEach(([key, val]) => {
      const sanitizedVal = sanitizeFirestoreData(val);
      if (sanitizedVal !== undefined) {
        result[key] = sanitizedVal;
      }
    });
    return result;
  }
  return value === undefined ? undefined : value;
};

/* ============================= FORNECEDORES ============================== */
export const getSuppliers = async () => {
  const snap = await getDocs(suppliersCollectionRef);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addSupplier = async (supplierData) => {
  let codeToUse = supplierData.friendlyCode;

  // Se o código não foi informado, vamos gerar automaticamente
  if (!codeToUse) {
    let nextNum = 1; // Começa em 1 se não achar nada

    try {
      // TENTATIVA 1: Busca Otimizada (Requer Índice no Firestore)
      const qLast = query(suppliersCollectionRef, orderBy('friendlyCode', 'desc'), limit(1));
      const snapLast = await getDocs(qLast);

      if (!snapLast.empty) {
        const lastData = snapLast.docs[0].data();
        const lastCode = lastData.friendlyCode;
        const parsed = parseInt(lastCode, 10);
        if (!isNaN(parsed)) {
          nextNum = parsed + 1;
        }
      }
    } catch (err) {
      console.warn("Index de ordenação ausente. Usando Fallback de Memória para gerar código.", err);
      
      // TENTATIVA 2: Fallback de Memória
      try {
        const allSnap = await getDocs(suppliersCollectionRef);
        let maxCode = 0;
        
        allSnap.forEach(doc => {
          const fc = doc.data().friendlyCode;
          if (fc) {
             const p = parseInt(fc, 10);
             if (!isNaN(p) && p > maxCode) {
               maxCode = p;
             }
          }
        });
        nextNum = maxCode + 1;
      } catch (fallbackErr) {
        console.error("Erro crítico no fallback de código:", fallbackErr);
        nextNum = Date.now().toString().slice(-4); 
      }
    }

    codeToUse = String(nextNum).padStart(3, '0');
  }

  // Verificação final de duplicidade
  if (codeToUse) {
    const qy = query(suppliersCollectionRef, where('friendlyCode', '==', codeToUse), limit(1));
    const existing = await getDocs(qy);
    if (!existing.empty) {
       throw new Error(`O código '${codeToUse}' já existe. Por favor, tente novamente.`);
    }
  }

  const payload = { 
    ...supplierData, 
    friendlyCode: codeToUse, 
    createdAt: serverTimestamp() 
  };

  const ref = await addDoc(suppliersCollectionRef, payload);
  return { id: ref.id, friendlyCode: codeToUse };
};

export const updateSupplier = async (id, updatedData) => {
  const ref = doc(db, 'suppliers', id);
  await updateDoc(ref, { ...updatedData, updatedAt: serverTimestamp() });
};

export const deleteSupplier = async (id) => {
  const ref = doc(db, 'suppliers', id);
  await deleteDoc(ref);
};

/* ============================== CATEGORIAS =============================== */
export const getCategories = async () => {
  const snap = await getDocs(categoriesCollectionRef);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addCategory = async (categoryData) => {
  const qy = query(categoriesCollectionRef, where('name', '==', categoryData.name), limit(1));
  const existing = await getDocs(qy);
  if (!existing.empty) throw new Error(`Categoria com nome '${categoryData.name}' já cadastrada.`);
  const ref = await addDoc(categoriesCollectionRef, { ...categoryData, createdAt: serverTimestamp() });
  return { id: ref.id, ...categoryData };
};

export const updateCategory = async (id, updatedData) => {
  const ref = doc(db, 'categories', id);
  await updateDoc(ref, { ...updatedData, updatedAt: serverTimestamp() });
};

export const deleteCategory = async (id) => {
  const ref = doc(db, 'categories', id);
  await deleteDoc(ref);
};

/* ================================ CLIENTES =============================== */
export const getCustomers = async () => {
  const snap = await getDocs(clientsCollectionRef);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addCustomer = async (customerData) => {
  const ref = await addDoc(clientsCollectionRef, {
    ...customerData,
    createdAt: serverTimestamp(),
    subscriptionPlan: customerData.subscriptionPlan || 'Aspirante'
  });
  return { id: ref.id, ...customerData };
};

export const updateCustomer = async (id, updatedData) => {
  const ref = doc(db, 'users', id);
  await updateDoc(ref, { ...updatedData, updatedAt: serverTimestamp() });
};

export const deleteCustomer = async (id) => {
  const ref = doc(db, 'users', id);
  await deleteDoc(ref);
};

/* ================================ COMPRAS ================================ */
export const addPurchase = async (purchaseData) => {
  const payloadWithType = {
    ...purchaseData,
    purchaseType: purchaseData?.purchaseType || 'OUTRO',
  };
  const safePurchase = sanitizeFirestoreData(payloadWithType);
  const ref = await addDoc(purchasesCollectionRef, safePurchase);
  return ref;
};

export const getPurchases = async () => {
  const snap = await getDocs(purchasesCollectionRef);
  return snap.docs.map(d => {
    const data = d.data() || {};
    return {
      id: d.id,
      ...data,
      purchaseType: data.purchaseType || 'OUTRO',
    };
  });
};

export const getPurchaseById = async (id) => {
  const ref = doc(db, 'purchases', id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  return {
    id: snap.id,
    ...data,
    purchaseType: data.purchaseType || 'OUTRO',
  };
};

export const updatePurchase = async (id, updatedData) => {
  const payloadWithType = {
    ...updatedData,
    purchaseType: updatedData?.purchaseType || 'OUTRO',
    updatedAt: serverTimestamp(),
  };
  const safeUpdate = sanitizeFirestoreData(payloadWithType);
  const ref = doc(db, 'purchases', id);
  await updateDoc(ref, safeUpdate);
};

export const updatePurchaseWithStockDeltas = async (id, updatedData) => {
  const payloadWithType = {
    ...updatedData,
    purchaseType: updatedData?.purchaseType || 'OUTRO',
    updatedAt: serverTimestamp(),
  };
  const safeUpdate = sanitizeFirestoreData(payloadWithType);
  const ref = doc(db, 'purchases', id);
  await updateDoc(ref, safeUpdate);
};

/* ================================= ALUGUEL =============================== */
export const createRental = async (rentalData) => {
  const ref = await addDoc(rentalsCollectionRef, { ...rentalData, createdAt: serverTimestamp() });
  return { id: ref.id, ...rentalData };
};

/* ====================== USUÁRIOS / STAFF ================== */
export const listUsers = async () => {
  const snap = await getDocs(staffCollectionRef);
  const users = snap.docs.map(d => {
    const data = d.data() || {};
    const createdAtISO =
      (data.createdAt && typeof data.createdAt.toDate === 'function')
        ? data.createdAt.toDate().toISOString()
        : (data.creationTime || '1970-01-01T00:00:00Z');
    return {
      uid: d.id,
      displayName: data.name || data.displayName || (data.email ? data.email.split('@')[0] : 'Sem Nome'),
      email: data.email || '',
      role: data.role || 'vendedor',
      permissions: data.permissions || {},
      creationTime: createdAtISO,
      disabled: !!data.disabled,
      ...data,
    };
  });
  return { users };
};

export const updateUser = async (arg1, arg2) => {
  if (arg1 && typeof arg1 === 'object' && arg1.uid) {
    const { uid, data } = arg1;
    const ref = doc(db, 'staff', uid);
    await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
    return { data: { message: 'Usuário atualizado com sucesso (staff).' } };
  }
  const id = arg1;
  const updatedData = arg2;
  const ref = doc(db, 'users', id);
  await updateDoc(ref, { ...updatedData, updatedAt: serverTimestamp() });
  return { data: { message: 'Perfil de usuário atualizado (users).' } };
};

export const createUser = async (userData) => {
  const createUserFunction = httpsCallable(functions, 'createUser');
  try {
    const result = await createUserFunction(userData);
    return result;
  } catch (error) {
    console.error("Erro detalhado ao chamar a Cloud Function 'createUser':", error);
    throw error;
  }
};

export const listAppUsers = async () => {
  const snap = await getDocs(usersCollectionRef);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const grantMySelfAdmin = async (userId) => {
  const ref = doc(db, 'users', userId);
  await updateDoc(ref, { roles: { admin: true }, updatedAt: serverTimestamp() });
  console.warn('ATENÇÃO: grantMySelfAdmin é placeholder.');
};

/* ============================== FINANCEIRO =============================== */
export const getFinancialCategories = async () => {
  const snap = await getDocs(financialCategoriesCollectionRef);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getFinancialExpensesByCategories = async (categoryIds, startDate, endDate) => {
  const catSnap = await getDocs(financialCategoriesCollectionRef);
  const categoryMap = new Map();
  catSnap.forEach(d => categoryMap.set(d.id, d.data().name));

  let qy = query(
    financialTransactionsCollectionRef,
    where('type', '==', 'expense'),
    where('categoryRef', 'in', categoryIds.map(id => doc(db, 'financial_categories', id))),
    orderBy('date', 'desc')
  );

  if (startDate && endDate) {
    qy = query(
      qy,
      where('date', '>=', Timestamp.fromDate(startDate)),
      where('date', '<=', Timestamp.fromDate(endDate))
    );
  }

  const snap = await getDocs(qy);
  return snap.docs.map(d => ({
    id: d.id,
    ...d.data(),
    date: d.data().date?.toDate(),
    categoryName: categoryMap.get(d.data().categoryRef?.id) || 'Desconhecida'
  }));
};

export const addAccountPayable = async (accountPayableData) => {
  const ref = await addDoc(accountsPayableCollectionRef, { ...accountPayableData, createdAt: serverTimestamp() });
  return { id: ref.id, ...accountPayableData };
};

export const getAccountsPayable = async (filters = {}) => {
  let qy = query(accountsPayableCollectionRef, orderBy('dueDate', 'asc'));
  if (filters.status)      qy = query(qy, where('status', '==', filters.status));
  if (filters.supplierId)  qy = query(qy, where('supplierRef', '==', doc(db, 'suppliers', filters.supplierId)));
  if (filters.startDate && filters.endDate) {
    qy = query(
      qy,
      where('dueDate', '>=', Timestamp.fromDate(filters.startDate)),
      where('dueDate', '<=', Timestamp.fromDate(filters.endDate))
    );
  }
  const snap = await getDocs(qy);
  return snap.docs.map(d => ({
    id: d.id,
    ...d.data(),
    dueDate: d.data().dueDate?.toDate(),
    issueDate: d.data().issueDate?.toDate(),
    paymentDate: d.data().paymentDate?.toDate()
  }));
};

export const updateAccountPayable = async (id, updatedData) => {
  const ref = doc(db, 'accounts_payable', id);
  await updateDoc(ref, { ...updatedData, updatedAt: serverTimestamp() });
};

export const deleteAccountPayable = async (id) => {
  const ref = doc(db, 'accounts_payable', id);
  await deleteDoc(ref);
};

// [GS-CORE]: --- MOTOR DE CONTAS A PAGAR (REFATORADO) ---
export const payAccount = async ({ accountId, originalAccount, paymentData }) => {
  // Batch write para garantir atomicidade: 
  // 1. Atualiza conta a pagar
  // 2. Cria transação legado
  // 3. Atualiza ou cria no Extrato 2.0 (financial_ledger)
  const batch = writeBatch(db);

  const accountRef = doc(db, 'accounts_payable', accountId);
  const paymentTimestamp = Timestamp.fromDate(new Date(paymentData.date));
  
  // 1. Update Collection 'accounts_payable'
  const accountUpdate = {
    status: 'paid',
    paymentDate: paymentTimestamp,
    paidAmount: paymentData.amount,
    paymentMethod: paymentData.method,
    category: paymentData.category || originalAccount.category, // Salva categoria corrigida
    updatedAt: serverTimestamp()
  };
  batch.update(accountRef, accountUpdate);

  // 2. [LEGADO] financial_transactions
  const legacyLedgerRef = doc(collection(db, 'financial_transactions'));
  const legacyEntry = {
    type: 'expense', 
    amount: paymentData.amount, 
    categoryRef: originalAccount.categoryRef || null, 
    description: `Pagamento de Conta: ${originalAccount.description || 'Sem descrição'}`,
    date: paymentTimestamp,
    originModule: 'accounts_payable',
    originId: accountId,
    supplierRef: originalAccount.supplierRef || null,
    paymentMethod: paymentData.method || 'Não informado',
    createdAt: serverTimestamp(),
    recordedBy: 'system_auto_pay'
  };
  batch.set(legacyLedgerRef, legacyEntry);

  // 3. [NOVO] financial_ledger (Extrato 2.0)
  // Verifica se já existe (update) ou cria novo
  const newLedgerRef = doc(collection(db, 'financial_ledger'));
  const newLedgerEntry = {
    amountGross: -Math.abs(paymentData.amount),
    amountNet: -Math.abs(paymentData.amount),
    description: `Pagamento: ${originalAccount.description}`,
    category: paymentData.category || originalAccount.category || 'Despesas Operacionais', 
    type: 'expense',
    status: 'paid', // [CRÍTICO]: Força status 'paid'
    paymentMethod: paymentData.method,
    dates: {
        issue: paymentTimestamp,
        payment: paymentTimestamp
    },
    createdAt: serverTimestamp(),
    source: 'accounts_payable_module',
    referenceId: accountId
  };
  batch.set(newLedgerRef, newLedgerEntry);

  try {
    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error("Erro crítico na transação de pagamento:", error);
    throw new Error("Falha ao registrar pagamento no sistema financeiro.");
  }
};

export const undoPayment = async (accountId) => {
  const accountRef = doc(db, 'accounts_payable', accountId);
  const ledgerCollectionRef = collection(db, 'financial_transactions');

  const qLedger = query(
    ledgerCollectionRef, 
    where('originModule', '==', 'accounts_payable'),
    where('originId', '==', accountId)
  );

  try {
    const batch = writeBatch(db);
    const ledgerSnap = await getDocs(qLedger);
    ledgerSnap.forEach((docLedger) => {
      batch.delete(docLedger.ref);
    });
    const accountUpdate = {
      status: 'pending',
      paymentDate: null,
      paidAmount: 0,
      paymentMethod: null,
      updatedAt: serverTimestamp()
    };
    batch.update(accountRef, accountUpdate);
    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error("Erro ao estornar pagamento:", error);
    throw new Error("Falha ao desfazer o pagamento.");
  }
};

// --- MOTOR DE CONTAS A RECEBER ---

export const addAccountReceivable = async (accountReceivableData) => {
  const ref = await addDoc(accountsReceivableCollectionRef, { ...accountReceivableData, createdAt: serverTimestamp() });
  return { id: ref.id, ...accountReceivableData };
};

export const getAccountsReceivable = async (filters = {}) => {
  let qy = query(accountsReceivableCollectionRef, orderBy('dueDate', 'asc'));
  if (filters.status)     qy = query(qy, where('status', '==', filters.status));
  if (filters.customerId) qy = query(qy, where('customerRef', '==', doc(db, 'users', filters.customerId)));
  if (filters.startDate && filters.endDate) {
    qy = query(
      qy,
      where('dueDate', '>=', Timestamp.fromDate(filters.startDate)),
      where('dueDate', '<=', Timestamp.fromDate(filters.endDate))
    );
  }
  const snap = await getDocs(qy);
  return snap.docs.map(d => ({
    id: d.id,
    ...d.data(),
    dueDate: d.data().dueDate?.toDate(),
    issueDate: d.data().issueDate?.toDate(),
    receiptDate: d.data().receiptDate?.toDate()
  }));
};

export const updateAccountReceivable = async (id, updatedData) => {
  const ref = doc(db, 'accounts_receivable', id);
  await updateDoc(ref, { ...updatedData, updatedAt: serverTimestamp() });
};

export const deleteAccountReceivable = async (id) => {
  const ref = doc(db, 'accounts_receivable', id);
  await deleteDoc(ref);
};

export const receiveAccount = async ({ accountId, originalAccount, receiptData }) => {
  const accountRef = doc(db, 'accounts_receivable', accountId);
  const ledgerCollectionRef = collection(db, 'financial_transactions');

  const receiptTimestamp = Timestamp.fromDate(new Date(receiptData.date));
  
  const accountUpdate = {
    status: 'received',
    receiptDate: receiptTimestamp,
    receivedAmount: receiptData.amount,
    receiptMethod: receiptData.method,
    updatedAt: serverTimestamp()
  };

  const ledgerEntry = {
    type: 'income',
    amount: receiptData.amount, 
    categoryRef: originalAccount.categoryRef || null, 
    description: `Recebimento: ${originalAccount.description || 'Sem descrição'}`,
    date: receiptTimestamp,
    originModule: 'accounts_receivable',
    originId: accountId,
    customerRef: originalAccount.customerRef || null,
    paymentMethod: receiptData.method || 'Não informado',
    createdAt: serverTimestamp(),
    recordedBy: 'system_auto_receive'
  };

  try {
    const batch = writeBatch(db);
    batch.update(accountRef, accountUpdate);
    const newLedgerRef = doc(ledgerCollectionRef); 
    batch.set(newLedgerRef, ledgerEntry);
    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error("Erro crítico na transação de recebimento:", error);
    throw new Error("Falha ao registrar recebimento.");
  }
};

export const undoReceive = async (accountId) => {
  const accountRef = doc(db, 'accounts_receivable', accountId);
  const ledgerCollectionRef = collection(db, 'financial_transactions');

  const qLedger = query(
    ledgerCollectionRef, 
    where('originModule', '==', 'accounts_receivable'),
    where('originId', '==', accountId)
  );

  try {
    const batch = writeBatch(db);
    const ledgerSnap = await getDocs(qLedger);
    ledgerSnap.forEach((docLedger) => {
      batch.delete(docLedger.ref);
    });
    
    const accountUpdate = {
      status: 'pending',
      receiptDate: null,
      receivedAmount: 0,
      receiptMethod: null,
      updatedAt: serverTimestamp()
    };
    batch.update(accountRef, accountUpdate);
    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error("Erro ao estornar recebimento:", error);
    throw new Error("Falha ao desfazer o recebimento.");
  }
};

export const getFinancialSummary = async (docId) => {
  const ref = doc(financialSummaryCollectionRef, docId);
  const snap = await getDoc(ref);
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  console.warn(`Documento de sumário financeiro '${docId}' não encontrado.`);
  return null;
};

export const updateFinancialSummary = async (docId, updatedData) => {
  const ref = doc(financialSummaryCollectionRef, docId);
  await updateDoc(ref, { ...updatedData, updatedAt: serverTimestamp() });
};

export const normalizeOrderDate = (order) => {
  const cands = [order?.createdAt, order?.date, order?.timestamp, order?.saleDate];
  for (const c of cands) {
    if (!c) continue;
    if (typeof c?.toDate === 'function') return c.toDate(); 
    if (typeof c === 'object' && typeof c.seconds === 'number') return new Date(c.seconds * 1000);
    const d = new Date(c);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

export const getOrdersByPeriod = async (startDate, endDate) => {
  const start = startDate ? new Date(startDate) : null;
  const end   = endDate   ? new Date(endDate)   : null;
  if (start) start.setHours(0, 0, 0, 0);
  if (end)   end.setHours(23, 59, 59, 999);

  const ordersCol = collection(db, 'orders');

  try {
    let qy = ordersCol;
    if (start || end) {
      const parts = [orderBy('createdAt', 'asc')];
      if (start) parts.push(where('createdAt', '>=', Timestamp.fromDate(start)));
      if (end)   parts.push(where('createdAt', '<=', Timestamp.fromDate(end)));
      qy = query(ordersCol, ...parts);
    }
    const snap = await getDocs(qy);
    const rows = snap.docs.map(d => {
      const data = d.data() || {};
      const createdAtDate = normalizeOrderDate(data);
      return { id: d.id, ...data, createdAtDate };
    });
    
    return rows.filter(o => {
      if (!start && !end) return true;
      if (!o.createdAtDate) return false;
      return (!start || o.createdAtDate >= start) && (!end || o.createdAtDate <= end);
    });
  } catch (err) {
    const snap = await getDocs(ordersCol);
    return snap.docs.map(d => {
      const data = d.data() || {};
      const createdAtDate = normalizeOrderDate(data);
      return { id: d.id, ...data, createdAtDate };
    }).filter(o => {
      if (!start && !end) return true;
      if (!o.createdAtDate) return false;
      return (!start || o.createdAtDate >= start) && (!end || o.createdAtDate <= end);
    });
  }
};